/**
 * Stage 8 — Block Model & Material Classification (strict gate).
 *
 * Embeds the existing BlockModelTab wholesale. The tab's block-summary
 * panel reads `class_summary.by_kind` from the block-model document,
 * which is the SAME source the Dashboard portfolio KPIs, the
 * MaterialClassManager, and the Resource Statement endpoint use — so
 * ore/waste/strip-ratio numbers stay identical across every surface.
 *
 * Completion hooks (backend-side):
 *   • POST /block-models/idw        → Stage 7 + Stage 8 complete
 *   • POST /block-models/kriging    → Stage 7 + Stage 8 complete
 *   • POST /material-classes/apply  → Stage 8 complete + 9-12 dirty (E-2)
 */
import { useEffect, useState } from "react";
import BlockModelTab from "@/components/tabs/BlockModelTab";
import AIAutopilotPanel from "../AIAutopilotPanel";
import api from "@/lib/api";
import { formatTonnage } from "@/components/ssot/OreFigures";
import StageModeSwitcher, { useStageMode } from "@/components/StageModeSwitcher";

// Iter 70 · Feb 2026: platform rebrand — no `kt`. Auto-scale to `Mt` or `t`.
function fmtT(t) {
  return formatTonnage(t, "auto", { decimals: 2 });
}

export default function Stage8BlockModelClassification({ projectId, onCompleted }) {
  const [_smMode, _setSmMode] = useStageMode(projectId, 8, "ai");
  const [bm, setBm] = useState(null);
  const [rs, setRs] = useState(null);
  const [pollActive, setPollActive] = useState(false);

  const loadSources = async () => {
    try {
      const b = await api.get(`/projects/${projectId}/block-models`);
      // pick the most recent BM
      const latest = (b.data || []).slice().sort((a, x) =>
        (x.created_at || "").localeCompare(a.created_at || ""))[0];
      setBm(latest || null);
      // Resource statement — 2nd consumer of class_summary
      const r = await api.get(`/projects/${projectId}/resource-statement`).catch(() => null);
      setRs(r?.data || null);
      // Session E-3 · SSoT audit trail. Log the reconciliation result so
      // any future drift is caught automatically. Fire-and-forget.
      if (latest && r?.data) {
        const cs = latest.class_summary || {};
        const ws = r.data.waste_summary || {};
        const matchOre   = Math.abs(Number(cs.total_ore_tonnage   || 0) - Number(ws.ore_tonnage   || 0)) < 1;
        const matchWaste = Math.abs(Number(cs.total_waste_tonnage || 0) - Number(ws.waste_tonnage || 0)) < 1;
        const matchStrip = Math.abs(Number(cs.strip_ratio         || 0) - Number(ws.strip_ratio   || 0)) < 0.001;
        api.post(`/projects/${projectId}/class-summary-audit/log`, {
          block_model_id: latest.id,
          match_ore: matchOre, match_waste: matchWaste, match_strip: matchStrip,
          stepper_ore:   Number(cs.total_ore_tonnage),
          stepper_waste: Number(cs.total_waste_tonnage),
          stepper_strip: Number(cs.strip_ratio),
          statement_ore:   Number(ws.ore_tonnage),
          statement_waste: Number(ws.waste_tonnage),
          statement_strip: Number(ws.strip_ratio),
        }).catch(() => { /* audit is best-effort */ });
      }
    } catch { /* silent */ }
  };

  useEffect(() => { if (projectId) loadSources(); }, [projectId]);
  useEffect(() => {
    if (!pollActive || !projectId) return undefined;
    const t = setInterval(() => { onCompleted?.(); loadSources(); }, 3500);
    const stop = setTimeout(() => setPollActive(false), 60_000);
    return () => { clearInterval(t); clearTimeout(stop); };
  }, [pollActive, projectId, onCompleted]);

  const cs = bm?.class_summary || {};
  const oreT   = Number(cs.total_ore_tonnage   || 0);
  const wasteT = Number(cs.total_waste_tonnage || 0);
  const stripR = cs.strip_ratio;

  // Second SSoT consumer — the resource-statement waste_summary reads from
  // the same class_summary field on the same block model, so identical
  // numbers here prove single-source-of-truth end-to-end.
  const rsWs = rs?.waste_summary || null;
  const bmMatch = rsWs && bm ? {
    ore: Math.abs(Number(rsWs.ore_tonnage || 0) - oreT) < 1,
    waste: Math.abs(Number(rsWs.waste_tonnage || 0) - wasteT) < 1,
  } : null;
  const allMatch = bmMatch && bmMatch.ore && bmMatch.waste;

  return (
    <div data-testid="stage8-content" className="space-y-4">
      <div className="flex items-center justify-between gap-3 pb-2 border-b border-neutral-100">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-medium">Input mode</div>
        <StageModeSwitcher mode={_smMode} onChange={_setSmMode} testidPrefix="stage8-mode" />
      </div>
      <AIAutopilotPanel
        projectId={projectId}
        title="AI Auto-Fill · Material classification"
        description="Scans your lithology + assays, proposes ore / waste / overburden rules from the block-model cutoff, and applies them in one click."
        autoFillPath="auto-fill/material-classes"
        executePath={`/projects/${projectId}/material-classes/auto-apply`}
        buildPayload={(f) => ({
          cutoff_grade: f.cutoff_grade,
          primary_element: f.primary_element,
        })}
        successLabel="Material classes applied — Stage 6 complete"
        buttonLabel="Auto-classify blocks"
        onSuccess={() => { setPollActive(true); onCompleted?.(); loadSources(); }}
        testid="stage8-autopilot"
      />

      <div className="border border-neutral-200 bg-neutral-50 rounded p-3 text-[12px] text-neutral-800">
        <span className="text-[#4CAF50] font-medium">Manual mode:</span>{" "}
        generate a block model (IDW/Kriging) or re-apply material class rules with
        your own inputs. Both flows auto-complete Stage 6 server-side.
        <button
          onClick={() => { setPollActive(true); onCompleted?.(); loadSources(); }}
          className="ml-3 text-[#4CAF50] hover:text-[#2E7D32] underline text-[11.5px]"
          data-testid="stage8-refresh-state">
          Refresh state
        </button>
      </div>

      {/* Single-source-of-truth KPI ribbon — reads directly from
          class_summary on the active block model. */}
      {bm && (
        <div className="grid grid-cols-4 gap-3" data-testid="stage8-kpi-ribbon">
          <SSotKPI label="Active model" value={bm.name || "—"} testid="stage8-kpi-model" />
          <SSotKPI label="Ore tonnage"   value={fmtT(oreT)}   mono testid="stage8-kpi-ore"  />
          <SSotKPI label="Waste tonnage (incl. overburden)" value={fmtT(wasteT)} mono testid="stage8-kpi-waste" />
          <SSotKPI label="Strip ratio (W/O)" value={stripR != null ? Number(stripR).toFixed(3) : "—"} mono testid="stage8-kpi-strip" />
        </div>
      )}

      {/* SSoT consistency banner — compares this stepper's KPI panel to
          the resource-statement endpoint. Both read class_summary. */}
      {bm && rsWs && (
        <div className="border rounded p-2.5 text-[11px] font-mono flex items-center gap-3"
             data-testid="stage8-consistency"
             style={{
               borderColor: allMatch ? "rgba(16,185,129,0.4)" : "rgba(239,68,68,0.4)",
               background:  allMatch ? "rgba(16,185,129,0.05)" : "rgba(239,68,68,0.05)",
             }}>
          <span className={allMatch ? "text-emerald-600" : "text-red-600"}>
            {allMatch
              ? "✓ Stepper KPIs match /resource-statement (single source: class_summary)"
              : "✗ Divergence detected between Stepper KPIs and /resource-statement"}
          </span>
          <span className="text-neutral-500">
            (statement: ore={fmtT(rsWs.ore_tonnage)} · waste={fmtT(rsWs.waste_tonnage)} · strip={Number(rsWs.strip_ratio || 0).toFixed(3)})
          </span>
        </div>
      )}

      {/* Full BlockModelTab — the existing 3D viewport + material class UI */}
      <div className="border border-neutral-200 rounded-md bg-neutral-50 overflow-hidden"
           data-testid="stage8-blockmodel-tab-wrapper">
        <BlockModelTab projectId={projectId} />
      </div>
    </div>
  );
}

function SSotKPI({ label, value, mono, testid }) {
  return (
    <div className="border border-neutral-200 rounded-md px-4 py-3.5 bg-neutral-50" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.12em] text-neutral-500">{label}</div>
      <div className={`text-[22px] font-medium mt-1 ${mono ? "font-mono tabular-nums" : ""}`}>{value}</div>
    </div>
  );
}
